/**
 * PowerSync lifecycle ring buffer. Records every construct / teardown /
 * connect transition of the PowerSyncDatabase singleton so a tester can SEE,
 * after the fact, whether the instance was recreated (the cause of "ExecuteSet:
 * database is locked (code 5)" — two instances colliding on imki.sqlite).
 *
 * Persisted in localStorage, NOT memory: the auth flaps that trigger a recreate
 * also tear the WebView/JS context down and back up, so an in-memory buffer
 * would be wiped exactly when we need it. Surfaced live in DebugDiagnostics and
 * included in the copy-all dump. Mirrors the error-buffer.ts pattern.
 */

export const PS_LIFECYCLE_KEY = 'zs_ps_lifecycle'
const MAX_ENTRIES = 40
const MAX_DETAIL_CHARS = 300

export type PsLifecycleEvent =
  | 'doc-load'            // a fresh document/JS context started (detail: nav type + path + markers)
  | 'create'              // a new PowerSyncDatabase was constructed (detail: "#N (platform)")
  | 'create-blocked'      // getPowerSync() refused to construct while a close was in flight
  | 'close-start'         // closePowerSync() began disconnect()+close()
  | 'close-done'          // closePowerSync() finished; instance released
  | 'close-coalesced'     // a concurrent close() joined the in-flight teardown
  | 'teardown-scheduled'  // PowerSyncProvider saw user=null, armed the debounce timer
  | 'teardown-cancelled'  // user returned before the debounce elapsed — instance reused
  | 'teardown-fired'      // debounce elapsed — real sign-out, tearing down
  | 'provider-active'     // PowerSyncProvider bound the context to a live instance
  | 'connect-resolved'    // connect() promise resolved
  | 'connect-rejected'    // connect() promise rejected (detail: message)
  | 'status'              // notable status change (detail: connected/downloadError/…)
  | 'crud-discard'        // upload connector dead-lettered a poisoned CRUD op (detail: op table id code)

export interface PsLifecycleEntry {
  ts: number
  event: PsLifecycleEvent
  detail?: string
}

export function logPsLifecycle(event: PsLifecycleEvent, detail?: string): void {
  if (typeof window === 'undefined') return
  try {
    const entry: PsLifecycleEntry = {
      ts: Date.now(),
      event,
      ...(detail ? { detail: detail.slice(0, MAX_DETAIL_CHARS) } : {}),
    }
    const list = readPsLifecycle()
    // Dedupe identical back-to-back events within 400ms (status ticks can
    // repeat) so the buffer stays readable.
    const last = list[list.length - 1]
    if (last && last.event === entry.event && last.detail === entry.detail && entry.ts - last.ts < 400) return
    list.push(entry)
    while (list.length > MAX_ENTRIES) list.shift()
    try {
      window.localStorage.setItem(PS_LIFECYCLE_KEY, JSON.stringify(list))
    } catch {
      try { window.localStorage.setItem(PS_LIFECYCLE_KEY, JSON.stringify([entry])) } catch { /* give up */ }
    }
    // Mirror to console so it also lands in the [PS-DIAG] stream when a
    // Web Inspector / logcat IS attached.
    console.log(`[PS-LIFECYCLE] ${event}${detail ? ` ${detail}` : ''}`)
  } catch {
    /* never let diagnostics throw */
  }
}

/**
 * Record that a fresh document/JS context just started. The lifecycle buffer
 * persists in localStorage, so reading back a sequence of `doc-load` entries
 * reveals how often (and WHY) the WebView is reloading — the suspected cause of
 * the native code-5 lock (each reload spins up a new PowerSyncDatabase while the
 * previous document's native SQLite connection is still open). The nav type
 * distinguishes a hard reload (`reload`) from a client→hard redirect
 * (`navigate`) from an OS/back-forward restore (`back_forward`). Call once, as
 * early as possible, from the app entry.
 */
export function logDocLoad(): void {
  if (typeof window === 'undefined') return
  try {
    let navType = 'unknown'
    try {
      const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
      if (nav?.type) navType = nav.type
    } catch { /* timing API unavailable */ }
    const search = window.location.search
    const markers = [
      /[?&]__cb=/.test(search) ? 'cb' : '',   // chunk-recovery cache-bust
      /[?&]nuked=/.test(search) ? 'nuked' : '',
    ].filter(Boolean).join(',')
    let ref = ''
    try { ref = document.referrer ? new URL(document.referrer).pathname : '' } catch { /* opaque referrer */ }
    logPsLifecycle(
      'doc-load',
      `${navType} path=${window.location.pathname}${markers ? ` [${markers}]` : ''}${ref ? ` ref=${ref}` : ''}`,
    )
  } catch { /* never let diagnostics throw */ }
}

export function readPsLifecycle(): PsLifecycleEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PS_LIFECYCLE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function clearPsLifecycle(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(PS_LIFECYCLE_KEY) } catch { /* noop */ }
}
